import configuration from '@sonifi/wb-core/services/configuration.service';
import apiService from '@sonifi/wb-core/services/api.service';
import { checkingSSO, setAuth, setIsLogin, setSSO } from '@sonifi/wb-user/store//userSlice';
import URI from 'urijs';
import oauth from './oauth.service';
import { getUser } from '@sonifi/wb-user/services/user.service';
import { cleanWBState } from '@sonifi/wb-core/services/store.service';
import { createUserPermissions } from '@sonifi/wb-user/services/permissions.service';
import { getGroups } from '@sonifi/wb-group/services/group.service';
import { GROUPS_KEYS } from '@sonifi/wb-group/store/groupsConstants';
import { getProducts } from '@sonifi/wb-products/services/products.service';
import { getHomeImages } from '@sonifi/wb-home/services/home.service';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import { STORAGE_KEYS } from '../constants/wbLoginConstants';
import { ROUTES_PATH } from '@sonifi/wb-core/routes/routesConstants';

const apiUri = configuration.get('apiUri');
const initiateEndpoint = new URI(apiUri).segment('initiate');
const authorizeEndpoint = new URI(apiUri).segment('authorize');
const exchangeEndpoint = new URI(apiUri).segment('token');
const currentUserEndpoint = 'users/current';

const initiate = () => {
  const endpoint = initiateEndpoint.toString();
  const options = {
    callbackUri: 'oob',
    ...configuration.get('oauthCredentials'),
  };

  return apiService.postRaw(endpoint, null, {
    headers: {
      Authorization: oauth.buildOAuthHeader('POST', endpoint, null, options),
    },
  });
};

const exchange = (responses) => {
  const endpoint = exchangeEndpoint.toString();
  const tokens = URI.parseQuery(responses[0].data);

  const options = {
    ...configuration.get('oauthCredentials'),
    resourceOwner: {
      oauth_token: tokens.oauth_token,
      oauth_token_secret: tokens.oauth_token_secret,
    },
    verifier: URI.parseQuery(responses[1].data).oauth_verifier,
  };

  return apiService.postRaw(endpoint, null, {
    headers: {
      Authorization: oauth.buildOAuthHeader('POST', endpoint, null, options),
    },
  });
};

const doAuthenticate = (boundAuth) => {
  const oauthCredentials = configuration.get('oauthCredentials');
  const initiatePromise = initiate();
  const authorizePromise = initiatePromise.then(boundAuth);

  return Promise.all([initiatePromise, authorizePromise])
    .then(exchange)
    .then((response) => {
      const newOauthCredentials = {
        ...oauthCredentials,
        resourceOwner: URI.parseQuery(response.data),
      };
      configuration.set('oauthCredentials', newOauthCredentials);
    })
    .then(() =>
      apiService.get(currentUserEndpoint).then(function (response) {
        configuration.set('userId', response.data.id);
        return response.data;
      }),
    );
};

const doAuthorize = (body, response) => {
  const endpoint = authorizeEndpoint
    .setSearch('oauth_token', URI.parseQuery(response.data).oauth_token)
    .toString();

  return apiService
    .post(endpoint, body, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
    .then((response) => {
      if (response?.data?.sso_redirect_url) {
        window.location = response.data.sso_redirect_url;
      }
      return response;
    });
};

const authorize = (email, password, response) => {
  const body = URI.buildQuery({
    email,
    password,
  });
  sessionStorage.setItem(STORAGE_KEYS.email, email);
  return doAuthorize(body, response);
};

const authorizeViaToken = (token, response) => {
  const body = URI.buildQuery({
    transfer_token: token,
  });
  return doAuthorize(body, response);
};

const authenticate = (email, password) =>
  doAuthenticate(authorize.bind(null, email, password));

export const authenticateViaToken = (token) =>
  doAuthenticate(authorizeViaToken.bind(null, token));

export const isAuthenticated = () =>
  Object.prototype.hasOwnProperty.call(
    configuration.get('oauthCredentials'),
    'resourceOwner',
  );

export const login =
  ({ email, password, usesSSO }) =>
  async (dispatch) => {
    try {
      dispatch(setIsLogin(true));
      const userLogged = await authenticate(
        email.toLowerCase().trim(),
        password,
      );
      const permissions = await dispatch(createUserPermissions(userLogged));
      const user = { ...userLogged, permissionsList: permissions };
      await Promise.all([
        dispatch(getHomeImages()),
        dispatch(
          setAuth({ authenticated: true, loginFailed: false, user, usesSSO }),
        ),
        dispatch(getGroups({ groupKey: GROUPS_KEYS.allGroups })),
        dispatch(getProducts()),
        dispatch(setIsLogin(false)),
      ]);
      return user;
    } catch (error) {
      console.error(error);
      dispatch(
        setAuth({
          authenticated: false,
          loginFailed: true,
          user: null,
          usesSSO,
        }),
      );
      dispatch(setIsLogin(false));
      return null;
    }
  };

const logoutSSO = async (redirectNoAuth) => {
  const body = {
    transfer_token: sessionStorage.getItem(STORAGE_KEYS.transferToken),
  };
  try {
    if (redirectNoAuth) {
      window.location.href = `/${ROUTES_PATH.NO_AUTH}`;
    } else {
      const response = await apiService.post(ENDPOINTS.SAML_SLO, body);
      window.location = response.data.slo_redirect_url;
    }
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const logout =
  ({ usesSSO = false, redirectNoAuth = false }) =>
  async (dispatch) => {
    try {
      await apiService.delete('tokens');
      if (usesSSO) {
        await logoutSSO(redirectNoAuth);
      }
      dispatch(cleanWBState());
      sessionStorage.clear();
      return true;
    } catch (error) {
      console.error(error);
      sessionStorage.clear();
      window.location.reload();
    }
  };

export const checkUserLogged = () => async (dispatch) => {
  const isLogged = isAuthenticated();
  if (isLogged) {
    const userId = configuration.get('userId');
    const user = await dispatch(getUser(userId));
    const usesSSO = !!sessionStorage.getItem(STORAGE_KEYS.transferToken);
    await dispatch(
      setAuth({ authenticated: true, loginFailed: false, user, usesSSO }),
    );
    return true;
  }
  return false;
};

export const getUserSSOPreference = (email) => async (dispatch) => {
  try {
    dispatch(checkingSSO(true));
    const userDomain = email.trim().toLowerCase().split('@')[1];
    const res = await apiService.get(ENDPOINTS.SAML_DOMAINS);
    const usesSSO = res.data.objects.includes(userDomain);
    dispatch(setSSO(usesSSO));
    dispatch(checkingSSO(false));
  } catch (error) {
    console.error(error);
    dispatch(setSSO(false));
    dispatch(checkingSSO(false));
  }
};

export const getAuthCode = () => {
  const params = new URLSearchParams(new URL(location.href).search);
  return params.get(STORAGE_KEYS.authCode) || null;
};

export const loginViaAuthCode = (authCode) => async (dispatch) => {
  try {
    dispatch(getHomeImages());
    const response = await apiService.get(currentUserEndpoint, {
      params: { code: authCode },
    });
    configuration.set('userId', response.data.id);
    const permissions = await dispatch(createUserPermissions(response.data));
    const user = { ...response.data, permissionsList: permissions };
    await Promise.all([
      dispatch(setAuth({ authenticated: true, loginFailed: false, user, usesSSO: true })),
      dispatch(getGroups({ groupKey: GROUPS_KEYS.allGroups })),
      dispatch(getProducts()),
    ]);
    const home = new URL(location.href).href.split('?')[0];
    history.replaceState({}, '', home);
  } catch (error) {
    console.error(error);
    sessionStorage.clear();
    window.location.href = `/${ROUTES_PATH.NO_AUTH}`;
  }
};
export const getTransferToken = () => {
  const url = new URL(location.href);
  const params = new URLSearchParams(url.search);
  const transferToken = params.get(STORAGE_KEYS.transferToken);

  let isValid = false;
  if (transferToken) {
    const email = sessionStorage.getItem(STORAGE_KEYS.email);
    const tokenData = oauth.parseJwt(transferToken);
    isValid = tokenData?.name_id === email && tokenData?.enabled;
    sessionStorage.setItem(STORAGE_KEYS.transferToken, transferToken);
  }
  return { transferToken, isValid };
};

export const loginSSO = (transferToken) => async (dispatch) => {
  try {
    dispatch(getHomeImages());
    const userLogged = await authenticateViaToken(transferToken);
    const permissions = await dispatch(createUserPermissions(userLogged));
    const user = { ...userLogged, permissionsList: permissions };
    await Promise.all([
      dispatch(
        setAuth({
          authenticated: true,
          loginFailed: false,
          user,
          usesSSO: true,
        }),
      ),
      dispatch(getGroups({ groupKey: GROUPS_KEYS.allGroups })),
      dispatch(getProducts()),
    ]);
    const url = new URL(location.href);
    const home = url.href.split('?')[0]; // Remove transfer_token - Go Home
    history.replaceState({}, '', home);
  } catch (error) {
    console.error(error);
    sessionStorage.clear();
    window.location.href = `/${ROUTES_PATH.NO_AUTH}`;
  }
};
